import { useStore } from '@tanstack/react-form';
import type { ComponentProps } from 'react';

import { DatePicker as BaseDatePicker } from '@/components/Form';
import { Field as BaseField, FieldError as BaseFieldError } from '@/components/Form/fields';
import {
  NumberInput as BaseNumberInput,
  NumberInputGroup as BaseNumberInputGroup,
  NumberInputInput as BaseNumberInputInput,
  NumberInputScrubArea as BaseNumberInputScrubArea,
  NumberInputTrigger as BaseNumberInputTrigger,
  PasswordInput as BasePasswordInput,
  SearchInput as BaseSearchInput,
  TextArea as BaseTextArea,
  TextInput as BaseTextInput,
  TokenizedTextArea as BaseTokenizedTextArea,
} from '@/components/Form/inputs';
import {
  Combobox as ComboboxPrimitive,
  ComboboxChips as ComboboxPrimitiveChips,
  ComboboxInput as ComboboxPrimitiveInput,
  type ComboboxChipsProps as ComboboxPrimitiveChipsProps,
  type ComboboxInputProps as ComboboxPrimitiveInputProps,
  type ComboboxProps as ComboboxPrimitiveProps,
} from '@/components/Form/pickers/primitives/combobox';
import {
  Select as SelectPrimitive,
  SelectTrigger as SelectPrimitiveTrigger,
  type SelectProps as SelectPrimitiveProps,
  type SelectTriggerProps as SelectPrimitiveTriggerProps,
} from '@/components/Form/pickers/primitives/select';
import {
  Checkbox as BaseCheckbox,
  DayChooser as BaseDayChooser,
  RadioGroup as BaseRadioGroup,
  Switch as BaseSwitch,
} from '@/components/Form/switches';

import { useFieldContext } from './form-context';

export type SelectFieldOption = {
  label: string;
  value: string;
};
type AutoDisableProps = {
  autoDisable?: boolean;
};

export type ErrorProps = Omit<ComponentProps<typeof BaseFieldError>, 'errors'>;

export const Error = (props: ErrorProps) => {
  const field = useFieldContext<unknown>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

  if (!isInvalid) {
    return null;
  }

  return <BaseFieldError {...props} errors={field.state.meta.errors} />;
};

export type FieldProps = Omit<ComponentProps<typeof BaseField>, 'data-invalid'>;

export const Field = (props: FieldProps) => {
  const field = useFieldContext<unknown>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

  return <BaseField {...props} data-invalid={isInvalid || undefined} />;
};

export type TextInputProps = Omit<
  ComponentProps<typeof BaseTextInput>,
  'onBlur' | 'onValueChange' | 'value'
> &
  AutoDisableProps;

export const TextInput = ({ disabled, autoDisable = true, ...props }: TextInputProps) => {
  const field = useFieldContext<string>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseTextInput
      {...props}
      aria-invalid={isInvalid || undefined}
      value={field.state.value}
      disabled={disabled || (autoDisable && isSubmitting)}
      onBlur={field.handleBlur}
      onValueChange={field.handleChange}
    />
  );
};

export type PasswordInputProps = Omit<
  ComponentProps<typeof BasePasswordInput>,
  'onBlur' | 'onValueChange' | 'value'
> &
  AutoDisableProps;

export const PasswordInput = ({ disabled, autoDisable = true, ...props }: PasswordInputProps) => {
  const field = useFieldContext<string>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BasePasswordInput
      {...props}
      aria-invalid={isInvalid || undefined}
      value={field.state.value}
      disabled={disabled || (autoDisable && isSubmitting)}
      onBlur={field.handleBlur}
      onValueChange={field.handleChange}
    />
  );
};

export type SearchInputProps = Omit<
  ComponentProps<typeof BaseSearchInput>,
  'onBlur' | 'onValueChange' | 'value'
> &
  AutoDisableProps;

export const SearchInput = ({ disabled, autoDisable = true, ...props }: SearchInputProps) => {
  const field = useFieldContext<string>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseSearchInput
      {...props}
      aria-invalid={isInvalid || undefined}
      value={field.state.value}
      disabled={disabled || (autoDisable && isSubmitting)}
      onBlur={field.handleBlur}
      onValueChange={field.handleChange}
    />
  );
};

export type TokenizedTextAreaProps = Omit<
  ComponentProps<typeof BaseTokenizedTextArea>,
  'onBlur' | 'onChange' | 'value'
> &
  AutoDisableProps;

export const TokenizedTextArea = ({
  disabled,
  autoDisable = true,
  ...props
}: TokenizedTextAreaProps) => {
  const field = useFieldContext<string[]>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseTokenizedTextArea
      {...props}
      aria-invalid={isInvalid || undefined}
      value={field.state.value}
      disabled={disabled || (autoDisable && isSubmitting)}
      onBlur={field.handleBlur}
      onChange={field.handleChange}
    />
  );
};

export type NumberInputProps = Omit<
  ComponentProps<typeof BaseNumberInput>,
  'onValueChange' | 'value'
> &
  AutoDisableProps;

export const NumberInput = ({ disabled, autoDisable = true, ...props }: NumberInputProps) => {
  const field = useFieldContext<number | null | undefined>();
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseNumberInput
      {...props}
      value={field.state.value}
      disabled={disabled || (autoDisable && isSubmitting)}
      onValueChange={field.handleChange}
    />
  );
};

export type NumberInputGroupProps = ComponentProps<typeof BaseNumberInputGroup>;

export const NumberInputGroup = (props: NumberInputGroupProps) => {
  return <BaseNumberInputGroup {...props} />;
};

export type NumberInputInputProps = ComponentProps<typeof BaseNumberInputInput>;

export const NumberInputInput = ({ onBlur, ...props }: NumberInputInputProps) => {
  const field = useFieldContext<number | null | undefined>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

  return (
    <BaseNumberInputInput
      {...props}
      aria-invalid={isInvalid || undefined}
      onBlur={(event) => {
        onBlur?.(event);
        field.handleBlur();
      }}
    />
  );
};

export type NumberInputScrubAreaProps = ComponentProps<typeof BaseNumberInputScrubArea>;

export const NumberInputScrubArea = (props: NumberInputScrubAreaProps) => {
  return <BaseNumberInputScrubArea {...props} />;
};

export type NumberInputTriggerProps = ComponentProps<typeof BaseNumberInputTrigger>;

export const NumberInputTrigger = (props: NumberInputTriggerProps) => {
  return <BaseNumberInputTrigger {...props} />;
};

export type TextAreaProps = Omit<
  ComponentProps<typeof BaseTextArea>,
  'onBlur' | 'onChange' | 'value'
> &
  AutoDisableProps;

export const TextArea = ({ disabled, autoDisable = true, ...props }: TextAreaProps) => {
  const field = useFieldContext<string>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseTextArea
      {...props}
      aria-invalid={isInvalid || undefined}
      value={field.state.value}
      disabled={disabled || (autoDisable && isSubmitting)}
      onBlur={field.handleBlur}
      onChange={(event) => field.handleChange(event.target.value)}
    />
  );
};

export type SelectProps<Value, Multiple extends boolean | undefined = false> = SelectPrimitiveProps<
  Value,
  Multiple
> &
  AutoDisableProps;

export const Select = <Value, Multiple extends boolean | undefined = false>({
  onChange,
  onOpenChange,
  autoDisable = true,
  disabled,
  ...props
}: SelectProps<Value, Multiple>) => {
  const field = useFieldContext<SelectPrimitiveProps<Value, Multiple>['value']>();
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <SelectPrimitive<Value, Multiple>
      {...props}
      disabled={disabled || (autoDisable && isSubmitting)}
      value={field.state.value}
      onValueChange={(value) => {
        field.handleChange(value ?? null);
        onChange?.(value ?? null);
      }}
      onOpenChange={(open, eventDetails) => {
        onOpenChange?.(open, eventDetails);
        if (!open) {
          field.handleBlur();
        }
      }}
    />
  );
};

export type SelectTriggerProps = SelectPrimitiveTriggerProps;

export const SelectTrigger = (props: SelectTriggerProps) => {
  const field = useFieldContext();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

  return (
    <SelectPrimitiveTrigger
      {...props}
      aria-invalid={isInvalid || undefined}
      onBlur={field.handleBlur}
    />
  );
};

export type ComboboxProps<
  Value,
  Multiple extends boolean | undefined = false,
> = ComboboxPrimitiveProps<Value, Multiple> & AutoDisableProps;

export const Combobox = <Value, Multiple extends boolean | undefined = false>({
  onChange,
  autoDisable = true,
  disabled,
  ...props
}: ComboboxProps<Value, Multiple>) => {
  const field = useFieldContext<ComboboxPrimitiveProps<Value, Multiple>['value']>();
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <ComboboxPrimitive<Value, Multiple>
      {...props}
      disabled={disabled || (autoDisable && isSubmitting)}
      value={field.state.value ?? undefined}
      onValueChange={(value) => {
        const nextValue = value ?? null;
        field.handleChange(nextValue);
        onChange?.(nextValue);
      }}
      onOpenChange={(open) => {
        if (!open) {
          field.handleBlur();
        }
      }}
    />
  );
};

export type ComboboxInputProps = ComboboxPrimitiveInputProps;

export const ComboboxInput = ({ onBlur, ...props }: ComboboxInputProps) => {
  const field = useFieldContext();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

  return (
    <ComboboxPrimitiveInput
      {...props}
      aria-invalid={isInvalid || undefined}
      onBlur={(event) => {
        onBlur?.(event);
        field.handleBlur();
      }}
    />
  );
};

export type ComboboxChipsProps = ComboboxPrimitiveChipsProps;

export const ComboboxChips = ({ onBlur, ...props }: ComboboxChipsProps) => {
  const field = useFieldContext();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

  return (
    <ComboboxPrimitiveChips
      {...props}
      aria-invalid={isInvalid || undefined}
      onBlur={(event) => {
        onBlur?.(event);
        field.handleBlur();
      }}
    />
  );
};

export type DatePickerProps = Omit<
  ComponentProps<typeof BaseDatePicker>,
  'onValueChange' | 'value'
> &
  AutoDisableProps;

export const DatePicker = ({ disabled, autoDisable = true, ...props }: DatePickerProps) => {
  const field = useFieldContext<Date | undefined>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseDatePicker
      {...props}
      aria-invalid={isInvalid || undefined}
      value={field.state.value}
      disabled={disabled || (autoDisable && isSubmitting)}
      onValueChange={(value) => {
        field.handleChange(value);
        field.handleBlur();
      }}
    />
  );
};

export type SwitchProps = ComponentProps<typeof BaseSwitch> & AutoDisableProps;

export const Switch = ({ disabled, autoDisable = true, ...props }: SwitchProps) => {
  const field = useFieldContext<boolean>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseSwitch
      {...props}
      aria-invalid={isInvalid || undefined}
      disabled={disabled || (autoDisable && isSubmitting)}
      checked={field.state.value}
      onBlur={field.handleBlur}
      onCheckedChange={(value) => field.handleChange(Boolean(value))}
    />
  );
};

export type CheckboxProps = Omit<
  ComponentProps<typeof BaseCheckbox>,
  'checked' | 'onCheckedChange'
> &
  AutoDisableProps;

export const Checkbox = ({ disabled, autoDisable = true, ...props }: CheckboxProps) => {
  const field = useFieldContext<boolean>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseCheckbox
      {...props}
      aria-invalid={isInvalid || undefined}
      disabled={disabled || (autoDisable && isSubmitting)}
      checked={field.state.value}
      onBlur={field.handleBlur}
      onCheckedChange={(value) => field.handleChange(Boolean(value))}
    />
  );
};

export type RadioGroupProps = Omit<
  ComponentProps<typeof BaseRadioGroup>,
  'onValueChange' | 'value'
> &
  AutoDisableProps;

export const RadioGroup = ({ disabled, autoDisable = true, ...props }: RadioGroupProps) => {
  const field = useFieldContext<string>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseRadioGroup
      {...props}
      aria-invalid={isInvalid || undefined}
      disabled={disabled || (autoDisable && isSubmitting)}
      value={field.state.value}
      onBlur={field.handleBlur}
      onValueChange={field.handleChange}
    />
  );
};

export type DayChooserProps = Omit<ComponentProps<typeof BaseDayChooser>, 'onChange' | 'value'> &
  AutoDisableProps;

export const DayChooser = ({ disabled, autoDisable = true, ...props }: DayChooserProps) => {
  const field = useFieldContext<string[]>();
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);

  return (
    <BaseDayChooser
      {...props}
      aria-invalid={isInvalid || undefined}
      disabled={disabled || (autoDisable && isSubmitting)}
      value={field.state.value}
      onBlur={field.handleBlur}
      onChange={(value) => {
        if (Array.isArray(value)) {
          field.handleChange(value);
        }
      }}
    />
  );
};
